import { useLocation } from 'react-router-dom';

import { moduleForPath } from '../lib/route-permissions';
import { useAuthStore, type UserProfile } from '../stores/auth';

const SUPER_ADMIN = 'SUPER_ADMIN';
const PLATFORM_ADMIN = 'PLATFORM_ADMIN';

/**
 * Toàn quyền, không kiểm từng mã: tài khoản mẫu `SUPER_ADMIN`, và realm role `PLATFORM_ADMIN` (ADR-0077 mục 5, như
 * Tenant Admin ở cổng tenant). Kể cả mã ngoài danh mục nền tảng mà màn hình dùng (`THUE_BAO`, `DICH_VU_XA`…).
 */
export function toanQuyen(user: UserProfile): boolean {
  return user.roles.includes(SUPER_ADMIN) || (user.vaiTroLop1?.includes(PLATFORM_ADMIN) ?? false);
}

/**
 * Hành động trong chuỗi quyền `<MODULE_CODE>.<ACTION>` (§6.4).
 *
 * `HUY` không phải `DELETE`: huỷ một kỳ thuê bao **đổi trạng thái**, không xoá
 * dòng, vì bảng kỳ cũng là lịch sử (BR-211). Hai việc khác nhau nên hai mã quyền
 * khác nhau, khớp `Quyen.java` của billing-service (ADR-0019).
 */
export type ActionCode =
  | 'READ'
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'EXPORT'
  | 'HUY'
  // License on-premise (ADR-0058): cấp và duyệt là hai quyền tách nhau để có bốn mắt; thu hồi là quyền riêng,
  // khớp `NguoiGoi` của license-service.
  | 'ISSUE'
  | 'APPROVE'
  | 'REVOKE';

/** Có bất kỳ quyền nào trên phân hệ đó. Không truyền phân hệ → `true`. */
export function usePermission(moduleCode?: string): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  if (toanQuyen(user)) return true;
  if (!moduleCode) return true;
  return user.permissions.some((p) => p.startsWith(`${moduleCode}.`));
}

/** Khớp chính xác `MODULE.ACTION`. */
export function useActionPermission(moduleCode: string, actionCode: ActionCode): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  if (toanQuyen(user)) return true;
  return user.permissions.includes(`${moduleCode}.${actionCode}`);
}

/**
 * Giải phân hệ từ đường dẫn hiện tại rồi khớp chính xác hành động.
 * Route không được gán phân hệ → `true`.
 */
export function useRoutePermission(actionCode: ActionCode): boolean {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const moduleCode = moduleForPath(pathname);

  if (!user) return false;
  if (toanQuyen(user)) return true;
  if (!moduleCode) return true;
  return user.permissions.includes(`${moduleCode}.${actionCode}`);
}

/** OR trên nhiều phân hệ. Mảng rỗng → `true`. */
export function useAnyPermission(moduleCodes: string[]): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  if (toanQuyen(user)) return true;
  if (moduleCodes.length === 0) return true;
  return moduleCodes.some((code) => user.permissions.some((p) => p.startsWith(`${code}.`)));
}

/** Quản trị tổ chức (realm role TENANT_ADMIN): cài module, và có mọi quyền của module đã cài (ADR-0077 mục 2). */
export function laQuanTriToChuc(user: UserProfile | null): boolean {
  return Boolean(user && (user.vaiTroLop1?.includes('TENANT_ADMIN') || user.roles.includes('vaiTro.quanTriToChuc')));
}

/**
 * Quyền của module đã cài (ADR-0096): Tenant Admin có hết theo ADR-0077, nên không phải chờ `me/context` nạp lại
 * sau khi vừa cài; người khác theo đúng mã quyền được gán.
 */
export function coQuyenModuleDaCai(user: UserProfile | null, code: string): boolean {
  return laQuanTriToChuc(user) || coMaQuyen(user, code);
}

export function coMaQuyen(user: UserProfile | null, code: string): boolean {
  if (!user) return false;
  return toanQuyen(user) || user.permissions.includes(code) || (user.quyenModule?.includes(code) ?? false);
}

/**
 * Khớp chính xác một mã quyền module `<module>:<tài nguyên>:<hành động>` (ADR-0075), ví dụ
 * `tri-thuc:tep:share`, `van-ban:template:create`. SDK ở backend so khớp cùng cách, không có ký tự
 * đại diện. Không đủ quyền thì **ẩn** nút (ADR-UI-0005).
 *
 * Mã đọc ở hai chỗ: `quyenModule` (nguyên văn `me/context`) và `permissions` (`tinhQuyenCongTenant`
 * nối các mã có dấu `:` vào). Tài khoản mẫu chỉ có `permissions`: có mã tri thức mẫu
 * (`QUYEN_TRI_THUC_MAU`), không có mã `van-ban`, nên màn văn bản dựa vào `useRoutePermission` như cũ.
 */
export function useModulePermission(code: string): boolean {
  const user = useAuthStore((s) => s.user);
  return coMaQuyen(user, code);
}

/** Nhiều mã quyền module một lần, cùng luật với `useModulePermission`: `{ tepSua: true, … }`. */
export function useModulePermissions<K extends string>(codes: Readonly<Record<K, string>>): Record<K, boolean> {
  const user = useAuthStore((s) => s.user);
  const ra = {} as Record<K, boolean>;
  for (const k of Object.keys(codes) as K[]) ra[k] = coMaQuyen(user, codes[k]);
  return ra;
}
